import { useRouter } from 'next/router'
import { useState, type FunctionComponent, useContext } from 'react'
import Modal from 'react-modal'
import { Button } from './Button'
import type { ButtonProps } from './Button'
import { JobsFormModal } from './JobsFormModal'
import { JobModalInfo } from './JobModalInfo'
import { usePageContext } from '../pages/[[...path]]'
import { useBodyScrollLock } from '../utilities/useBodyScrollLock'
import { useTranslation } from '../utilities/useTranslation'
import { PageThemeContext } from '../contexts/pageThemeContext'

export const FORM_MODAL_QUERY_PARAM = 'formular'

const JOBS_FORM_MODAL_STYLES = {
	content: {
		inset: '0',
		border: 'none',
		borderRadius: '0',
	},
	overlay: {
		zIndex: 2,
	},
} as const

export type JobApplyButtonProps = {
	variant?: ButtonProps['variant']
	className?: string
	align?: 'center' | 'start'
	/** Sync the modal open state with the `?formular=true` url param (used for the primary hero button / deep links). */
	useUrlParam?: boolean
}

export const JobApplyButton: FunctionComponent<JobApplyButtonProps> = ({
	variant,
	className,
	align = 'center',
	useUrlParam = false,
}) => {
	const page = usePageContext()
	const theme = useContext(PageThemeContext)
	const applyButtonVariant = variant ?? (theme?.name?.toLowerCase() === 'red' ? 'white' : 'red')
	const jobsFormModal = page.data.getJobsFormLocale ?? null
	const jobPageData = page.data.getLinkable?.jobPage ?? null
	const router = useRouter()
	const [localModalOpen, setLocalModalOpen] = useState(false)
	const isModalOpen = useUrlParam ? router.query[FORM_MODAL_QUERY_PARAM] !== undefined : localModalOpen
	const setIsModalOpen = (open: boolean) => {
		if (!useUrlParam) {
			setLocalModalOpen(open)
			return
		}
		const query = { ...router.query }
		if (open) {
			query[FORM_MODAL_QUERY_PARAM] = 'true'
		} else {
			delete query[FORM_MODAL_QUERY_PARAM]
		}
		router.push({ query }, undefined, { shallow: true, scroll: false })
	}
	const applyButtonLabel = useTranslation('jobApplyButton.applyButtonLabel')

	useBodyScrollLock(Boolean(jobsFormModal && isModalOpen))

	if (!jobsFormModal) {
		return null
	}

	return (
		<div style={{ display: 'flex', justifyContent: align, alignItems: 'center', padding: '8px 0' }}>
			<Button type="button" variant={applyButtonVariant} className={className} onClick={() => setIsModalOpen(true)}>
				{applyButtonLabel}
			</Button>
			<Modal isOpen={isModalOpen} style={JOBS_FORM_MODAL_STYLES} onRequestClose={() => setIsModalOpen(false)}>
				<JobsFormModal
					jobsFormModal={jobsFormModal}
					onRequestClose={() => setIsModalOpen(false)}
					customModalData={
						jobPageData
							? {
									jobPageId: jobPageData.id,
									content: <JobModalInfo jobForm={jobPageData} />,
								}
							: undefined
					}
				/>
			</Modal>
		</div>
	)
}
